import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import reduce from 'lodash/reduce';
import some from 'lodash/some';
import forEach from 'lodash/forEach';
import set from 'lodash/set';

import { ui } from '@/constants';
import { CustomerProfile, FormField, Order } from '@/models';
import { makeFormField } from '@/helpers/forms';
import { FeedbackPostBody } from '@/models/feedback';
import { PillOptions, Pill } from './CmgPillSelector/cmg-pill-selector';
import { convertLocalToUTC } from '@/helpers/util';
import { CountryCode } from '@/store/ui';

const DEFAULT_ADDITIONAL_TEXT = `What wasn't quite right?`;

const RATING_TEXTS = [
  { text: 'HIGHLY DISSATISFIED', color: ui.colors.red, additionalText: DEFAULT_ADDITIONAL_TEXT, additionalTextColor: ui.colors.brownDark, fontWeight: 'bold' },
  { text: 'DISSATISFIED', color: ui.colors.red, additionalText: DEFAULT_ADDITIONAL_TEXT, additionalTextColor: ui.colors.brownDark, fontWeight: 'bold' },
  { text: 'NEUTRAL', color: ui.colors.black, additionalText: DEFAULT_ADDITIONAL_TEXT, additionalTextColor: ui.colors.brownDark, fontWeight: 'bold' },
  { text: 'SATISFIED', color: ui.colors.goldNew, additionalText: DEFAULT_ADDITIONAL_TEXT, additionalTextColor: ui.colors.brownDark, fontWeight: 'bold' },
  { text: 'HIGHLY SATISFIED', color: ui.colors.goldNew, additionalText: `We're happy you're happy`, additionalTextColor: ui.colors.goldNew, fontWeight: 'normal' },
];

const DEFAULT_PAGE_STATE = {
  rating: 0,
  ratingText: '',
  ratingTextColor: '',
  additionalText: '',
  additionalTextColor: '',
  errorText: '',
  errorTextColor: '',
  fontWeight: '',
};

// Must map back to old keys/tags in order to keep reporting in sync
const ISSUE_TAG_MAPPINGS = [
  { updatedTag : 'wrongMissingItemS', oldTag: 'accuracy', isDeliveryTag: true, isPickUpTag: true },
  { updatedTag : 'orderWasLate', oldTag: 'deliveryTime', isDeliveryTag: true, isPickUpTag: false },
  { updatedTag : 'orderWasLate', oldTag: 'orderNotReady', isDeliveryTag: false, isPickUpTag: true },
  { updatedTag : 'somethingElse', oldTag: 'other', isDeliveryTag: true, isPickUpTag: true }
]

const MAX_ADDITIONAL_DETAILS_CHARS = 200;

const DEFAULT_ADDITIONAL_DETAILS_PLACEHOLDER = 'Please let us know any additional details so we can work to make it right';

let orderDateTime = '';

@Component({
  name: 'CmgFeedbackModal',
})
export default class CmgFeedbackModal extends Vue {
  transitioning = false;
  additionalDetails: FormField<string> = makeFormField();
  pills: PillOptions = {};
  contactMe = false;
  submitting = false;
  submitError = false;
  submitSuccess = false;
  rating = 0;
  ratingText = '';
  ratingTextColor = '';
  additionalText = '';
  additionalTextColor = '';
  errorText = '';
  errorTextColor = ui.colors.scarlet;
  fontWeight = '';

  @Watch('visible', { immediate: true, deep: true })
  async visibleChanged(visible: boolean) {
    if (visible) {
      const order = this.$appStore.state.order.lastOrder.order;
      if (order && !order.order.orderDateTimeLocal) {
        // Intentionally left non-blocking
        this.refreshOrder(order.order.orderId);
      }

      Vue.nextTick(() => {
        setTimeout(() => (this.$refs.mainTitle as HTMLElement).focus(), 500);
      });
    }
  }

  mounted() {
    this.additionalDetails.placeholder = DEFAULT_ADDITIONAL_DETAILS_PLACEHOLDER;
  }

  get charCount(): string {
    return `${this.additionalDetails.value.length} / ${MAX_ADDITIONAL_DETAILS_CHARS}`;
  }

  get visible() {
    return this.$appStore.state.ui.modals.feedback.visible;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth < ui.breakpoints.tablet;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get videos() {
    return {
      sadAvocado: this.$appStore.state.cms.images.feedback.sadAvocado.default,
      mehAvocado: this.$appStore.state.cms.images.feedback.mehAvocado.default,
      happyAvocado: this.$appStore.state.cms.images.feedback.happyAvocado.default,
    };
  }

  get subTextVisibility() {
    return { visibility: this.rating < 5 ? 'visible' : 'hidden' };
  }

  get contactMeTextVisiblity() {
    return { visibility: this.contactMe ? 'visible' : 'hidden' };
  }

  get args() {
    return this.$appStore.state.ui.modals.feedback.args;
  }

  async refreshOrder(orderId: string) {
    const order = await this.$services.order.getOrder(orderId, false);
    if (order && order.order.orderDateTimeLocal) {
      orderDateTime = convertLocalToUTC(order.order.orderDateTimeLocal);
    }
  }

  ratingSelected(rating: number) {
    this.rating = rating;
    this.updateStateOnSelect(rating);
    this.updateStateByRating(rating);
    Vue.nextTick(() => {
      setTimeout(() => (this.$refs.ratingText as HTMLElement).focus(), 500);
    });
  }

  ratingHovered(rating: number) {
    this.updateStateByRating(rating);
  }

  ratingUnhovered() {
    this.updateStateByRating(this.rating);
  }

  updateStateOnSelect(rating: number) {
    if (rating) {
      this.additionalText = RATING_TEXTS[rating - 1].additionalText || '';
      this.additionalTextColor = RATING_TEXTS[rating - 1].additionalTextColor || '';
      this.fontWeight = RATING_TEXTS[rating - 1].fontWeight;
    }

    //set text area hint text
    if (rating == 5) {
      this.additionalDetails.error = '';
      this.additionalDetails.placeholder = `Anything else you'd like to let us know?`;
      this.contactMe = false;
    } else {
      this.additionalDetails.placeholder = DEFAULT_ADDITIONAL_DETAILS_PLACEHOLDER;
    }
  }

  updateStateByRating(rating: number) {
    if (rating) {
      this.ratingText = RATING_TEXTS[rating - 1].text;
      this.ratingTextColor = RATING_TEXTS[rating - 1].color;
      this.errorText = '';
    } else {
      this.clearPageState();
    }
  }

  clearPageState() {
    this.additionalText = DEFAULT_PAGE_STATE.additionalText;
    this.rating = DEFAULT_PAGE_STATE.rating;
    this.ratingText = DEFAULT_PAGE_STATE.ratingText;
    this.ratingTextColor = DEFAULT_PAGE_STATE.ratingTextColor;
    this.fontWeight = DEFAULT_PAGE_STATE.fontWeight;
    this.errorText = DEFAULT_PAGE_STATE.errorText;
  }

  onInputAdditionalDetails(e: Event) {
    if (e.target) {
      const target = e.target as HTMLInputElement;
      this.additionalDetails.value = target.value.substr(0, MAX_ADDITIONAL_DETAILS_CHARS);
      target.value = this.additionalDetails.value;
    }
  }

  onFocusAdditionalDetails() {
    this.additionalDetails.error = '';
  }

  pillsUpdated(pills: PillOptions) {
    this.pills = pills;
    this.errorText = '';
    if (!this.pills.other.selected) {
      this.additionalDetails.error = '';
    }
  }

  pillSelected(pills: PillOptions): boolean {
    return some(pills, 'selected');
  }

  toggleContactMe() {
    this.contactMe = !this.contactMe;
  }

  async onClickSubmit() {
    if (!this.rating) {
      // Prevent user from submitting without a rating
      return (this.errorText = 'Please select a rating to submit');
    }

    // Prevent user from submitting with less than 5 stars and no pills selected
    if (this.rating != 0 && this.rating != 5 && !this.pillSelected(this.pills)) {
      return (this.errorText = 'Please make a selection');
    }

    // Prevent submitting any selected pills if 5 stars selected
    if (this.rating == 5) {
      forEach(this.pills, pill => set(pill, 'selected', false));
    }

    // Prevent user from submitting if Something Else pill selected and no details filled
    if (this.pills.other && this.pills.other.selected && !this.additionalDetails.value && this.rating != 5) {
      return (this.additionalDetails.error = 'Please let us know any additional details so we can work to make it right (required)');
    }

    if (!this.$appStore.state.order.lastOrder.order) {
      this.onClickClose();
      this.$services.ui.setModalVisibility('globalErrorToast', true);
      return this.$services.logger.unreachable('Last order was null when trying to submit feedback');
    }

    // Build POST Feedback body object
    const order = this.$appStore.state.order.lastOrder.order.order;
    const customer = this.$appStore.state.customer;
    const email = customer.profile ? customer.profile.email : '';
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    let loyaltySubscriptionId;
    if (customer.profile) {
      const loyaltyProfile = this.$services.loyalty.getLoyaltyProfile(customer.profile);
      if (loyaltyProfile) {
        loyaltySubscriptionId = loyaltyProfile.memberId;
      }
    }

    const isGuest = customer.profile ? customer.profile.isGuest : true;

    // Grabs selected pills in the form of ['taste', 'friendliness', 'other', 'easeOfOrdering']
    const issueTags = this.getIssueTags(order);

    let deliveryEstimateDateTime;
    let deliveryLocationZip;
    if (order.delivery) {
      deliveryLocationZip = order.delivery.dropOffAddressZipCode;
      deliveryEstimateDateTime = this.$store.state.order.pendingOrderDeliveryInfo.quotedDeliveryTime;
    }

    // If the order time wasn't grabbed on mount by refreshOrder -> attempt to use cached order time -> yolo it send fresh date it's close enough
    if (!orderDateTime) {
      orderDateTime = convertLocalToUTC(order.orderDateTimeLocal) || new Date().toISOString();
    }

    const feedbackBody: FeedbackPostBody = {
      orderId: order.orderId,
      orderDateTime,
      orderTotal: order.orderTotalAmount,
      restaurantId: order.restaurantId,
      email,
      loyaltySubscriptionId,
      isGuest,
      feedback: {
        overallRating: this.rating,
        issueTags,
        details: this.additionalDetails.value,
      },
      contactRequested: this.contactMe,
      channel: 'web',
      orderType: order.delivery ? 'delivery' : 'pickup',
      firstName: customer.profile ? customer.profile.firstName : '',
      lastName: customer.profile ? customer.profile.lastName : '',
      restaurantTimezoneOffset: restaurant ? restaurant.currentTimezoneOffset : 0,
      bagPickupLocationId: order.bagPickupLocationId,
      requestType: 'order',
      region: this.$appStore.state.ui.countryProfile.countryCode === CountryCode.GB ? "UK" : this.$appStore.state.ui.countryProfile.countryCode
    };

    if (order.delivery) {
      //Mobile is sending these times in local, so we will as well 
      //so the Feedback API can handle them correctly
      feedbackBody.deliveryEstimateDateTime = deliveryEstimateDateTime;
      feedbackBody.deliveryLocationZip = deliveryLocationZip;
    } else {
      feedbackBody.pickupEstimateDateTime = order.pickupDateTimeLocal;
    }

    try {
      this.submitting = true;
      const response = await this.$services.feedback.postFeedback(feedbackBody);
      this.$services.feedback.setFeedback(response);
      this.submitSuccess = true;
    } catch (e: any) {
      delete feedbackBody.email; // Remove PII
      this.$services.logger.error('Error submitting feedback', { ...e, feedbackBody });
      this.submitError = true;
    } finally {
      this.submitting = false;
    }
  }

  onClickClose(clearState = false) {
    if (clearState || this.submitSuccess) {
      this.resetState();
    }
    this.$services.ui.setModalVisibility('feedback', false);
    this.$services.ui.focusBack();
  }

  onClickContactSupport() {
    this.onClickClose(true);
    window.location.href = this.$services.config.getSupportUrl();
  }

  resetState() {
    this.pills = {};
    this.transitioning = false;
    this.additionalDetails = makeFormField();
    this.contactMe = false;
    this.submitting = false;
    this.submitError = false;
    this.submitSuccess = false;
    this.clearPageState();
  }

  onTransitionEnd() {
    this.additionalDetails.error = '';
    this.transitioning = false;
  }

  getIssueTags(order: Order): string[] {
    const selectedTags = reduce(this.pills, (accu: string[], pill: Pill, key: string) => (pill.selected ? [...accu, key] : accu), []);
    const tagMappings = order.delivery ? ISSUE_TAG_MAPPINGS.filter(x => x.isDeliveryTag) : ISSUE_TAG_MAPPINGS.filter(x => x.isPickUpTag);

    let mappedTags: string[] = [];
    selectedTags.forEach(t => {
      const tagMapping = tagMappings.find(m => m.updatedTag == t);
      mappedTags.push(tagMapping ? tagMapping.oldTag : t);
    });

    return mappedTags;
  }
}
